import { Html } from '@react-three/drei'
import { useMemo, useState } from 'react'

import { getShelfQuaternion } from '@components/storeMap/floor/3d/util'
import ShelfInfoPopoverContent from '@components/storeMap/floor/ShelfInfoPopoverContent'
import { useRegisterShelf } from '@components/storeMap/floor/ShelfRegistry'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'

import type { ThreeEvent } from '@react-three/fiber/dist/declarations/src/core/events'
import type { TransformedShelf } from '@state/shelf/types'

interface ShelfProps {
  shelf: TransformedShelf
  onClick: () => void
  onMouseOver: () => void
  onMouseOut: () => void
  fill?: string
}

const shelfColorDefault = '#BDCAD6'

function Shelf({ shelf, fill, onClick, onMouseOver, onMouseOut }: ShelfProps) {
  const [isHovered, setIsHovered] = useState(false)
  const { activeLayers } = useStoreMapContext()
  const shelfRef = useRegisterShelf(shelf.name)

  const myFill = fill ?? shelfColorDefault
  const { width, height, depth, rotation, center } = shelf

  const position = [center.x, height / 2, center.y] as const
  const quaternion = useMemo(() => getShelfQuaternion(rotation), [rotation])

  function clickHandler(e: ThreeEvent<MouseEvent>) {
    e.stopPropagation()
    onClick()
  }

  function onPointerOverHandler(e: ThreeEvent<PointerEvent>) {
    e.stopPropagation()
    onMouseOver()
    setIsHovered(true)
  }

  function onPointerOutHandler(e: ThreeEvent<PointerEvent>) {
    e.stopPropagation()
    onMouseOut()
    setIsHovered(false)
  }

  // little hack to counteract z-fighting when shelves are colored differently next to another shelf
  // by making sure there will be no adjacent shelves with the same height
  let myHeight = height
  if (fill) myHeight *= 1.02

  return (
    <group position={position}>
      <mesh
        ref={shelfRef}
        quaternion={quaternion}
        castShadow={true}
        receiveShadow={true}
        onClick={clickHandler}
        onPointerOver={onPointerOverHandler}
        onPointerOut={onPointerOutHandler}
      >
        <boxGeometry args={[width, myHeight, depth]} />
        <meshStandardMaterial color={myFill} />
      </mesh>

      {isHovered && (
        <Html style={{ pointerEvents: 'none' }}>
          <div className='rounded-sm bg-bg-primary-solid p-2 whitespace-nowrap text-white shadow-lg'>
            <ShelfInfoPopoverContent shelf={shelf} activeLayers={activeLayers} />
          </div>
        </Html>
      )}
    </group>
  )
}

export default Shelf